import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Badge from '../../../../components/Badge';
import TableBox from '../../../../components/TableBox';
import { TableContextProvider } from '../../../../interfaces/common';
import VerifyDataRow from './VerifyDataRow';
import StockMappingModal from '../../shared/StockMappingModal';
import { SortOrder } from '../../../../enums';
import useFetch from '../../../../hooks/useFetch';
import MonthlyStockService from '../../../../services/monthlyStock.service';
import { buildHeadCells } from '../../../../utils/methods';
import { VALIDATION_FIELD_MAPPING } from '../../../../utils/appConstant';

interface VerifyDataStepProps {
  monthlyStockId: number;
}

const VerifyDataStep: React.FC<VerifyDataStepProps> = ({ monthlyStockId }) => {
  const { t } = useTranslation();

  const [order, setOrder] = useState<SortOrder>(SortOrder.asc);
  const [orderBy, setOrderBy] = useState('stockist_product');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const [summary, setSummary] = useState<any>({});
  const [rows, setRows] = useState<any[]>([]);
  const [counts, setCounts] = useState({ linked: 0, unmapped: 0 });
  const [total, setTotal] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [selectedStockistProductId, setSelectedStockistProductId] = useState<number | null>(null);
  const [selectedStockistProductName, setSelectedStockistProductName] = useState('');

  const handleMapProduct = useCallback((stockistProductId: number, stockistProductName: string) => {
    setSelectedStockistProductId(stockistProductId);
    setSelectedStockistProductName(stockistProductName);
    setMapModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setMapModalOpen(false);
    setSelectedStockistProductId(null);
    setSelectedStockistProductName('');
  }, []);

  const handleMapped = useCallback(() => {
    setMapModalOpen(false);
    setSelectedStockistProductId(null);
    setSelectedStockistProductName('');
    setRefreshKey((prev) => prev + 1);
  }, []);

  useFetch({
    service: MonthlyStockService,
    func: 'getSummary',
    payload: { id: monthlyStockId },
    dependency: [monthlyStockId],
    onSuccess: (response) => {
      setSummary(response ?? {});
    },
  });

  const { loading } = useFetch({
    service: MonthlyStockService,
    func: 'getDetails',
    payload: { id: monthlyStockId, page, perPage, orderBy, order },
    dependency: [monthlyStockId, page, perPage, orderBy, order, refreshKey],
    onSuccess: (response) => {
      const payload = response ?? {};
      setRows(payload.data ?? []);
      setCounts(payload.counts ?? { linked: 0, unmapped: 0 });
      setTotal(payload.pagination?.total ?? 0);
    },
  });

  const summaryItems = [
    {
      icon: 'ri-store-2-line',
      label: t('monthlyStock.addEdit.pageTexts.verifyData.summary.stockist'),
      value: summary.stockist || '-',
    },
    {
      icon: 'ri-git-branch-line',
      label: t('monthlyStock.addEdit.pageTexts.verifyData.summary.branch'),
      value: summary.branch || '-',
    },
    {
      icon: 'ri-calendar-line',
      label: t('monthlyStock.addEdit.pageTexts.verifyData.summary.period'),
      value: summary.period || '-',
    },
    {
      icon: 'ri-file-settings-line',
      label: t('monthlyStock.addEdit.pageTexts.verifyData.summary.template'),
      value: summary.template || '-',
    },
  ];

  const headCells = buildHeadCells(
    [
      VALIDATION_FIELD_MAPPING.stockist_product,
      VALIDATION_FIELD_MAPPING.op_qty,
      VALIDATION_FIELD_MAPPING.op_qty_value,
      VALIDATION_FIELD_MAPPING.received_qty,
      VALIDATION_FIELD_MAPPING.received_qty_value,
      VALIDATION_FIELD_MAPPING.issued_qty,
      VALIDATION_FIELD_MAPPING.issued_qty_value,
      VALIDATION_FIELD_MAPPING.closing_qty,
      VALIDATION_FIELD_MAPPING.closing_qty_value,
      VALIDATION_FIELD_MAPPING.company_product,
      VALIDATION_FIELD_MAPPING.mapping,
    ],
    'monthlyStockVerifyList'
  );

  const tableContext: TableContextProvider = {
    headCells,
    row: VerifyDataRow,
    order,
    orderBy,
    page,
    perPage,
    total,
    data: rows,
    loading,
    setOrder,
    setOrderBy,
    setPage,
    setPerPage,
    pagination: true,
    rowProps: {
      onMapProduct: handleMapProduct,
    },
  };

  return (
    <>
      {/* Summary Card */}
      <div className="card mb-4">
        <div className="card-body py-4">
          <div className="row g-4">
            {summaryItems.map((item) => (
              <div key={item.label} className="col-md-3 col-sm-6">
                <div className="d-flex align-items-center">
                  <div className="avatar avatar-sm me-3 flex-shrink-0">
                    <div className="avatar-initial rounded bg-label-primary">
                      <i className={`${item.icon} ri-18px`}></i>
                    </div>
                  </div>
                  <div>
                    <small className="text-muted d-block">{item.label}</small>
                    <span className="fw-semibold text-body">{item.value}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Table Card */}
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center pb-3">
          <h5 className="mb-0 d-flex align-items-center">
            <i className="ri-list-check-2-line me-2 text-primary"></i>
            {t('monthlyStock.addEdit.pageTexts.verifyData.title')}
          </h5>
          <div className="d-flex gap-2">
            <Badge
              text={`${counts.linked} ${t('monthlyStock.addEdit.pageTexts.verifyData.table.status.linked')}`}
              variant="success"
            />
            {counts.unmapped > 0 && (
              <Badge
                text={`${counts.unmapped} ${t('monthlyStock.addEdit.pageTexts.verifyData.table.status.unmapped')}`}
                variant="warning"
              />
            )}
          </div>
        </div>
        <div className="card-body p-0">
          <TableBox tableValues={tableContext} />
        </div>
      </div>

      <StockMappingModal
        open={mapModalOpen}
        monthlyStockId={monthlyStockId}
        stockistProductId={selectedStockistProductId}
        stockistProductName={selectedStockistProductName}
        onClose={handleModalClose}
        onMapped={handleMapped}
      />
    </>
  );
};

export default VerifyDataStep;
